import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Plane, Clock, MapPin, Info, User, Mail, Phone, Edit2, Check, X, Copy, Activity, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { updateBusiness4FlyoCheckIn, updateBusiness4FlyoTicket } from '../../services/api';
import CheckInStatusPresets from '../CheckInStatusPresets';
import { getStatusTailwindClasses } from '../../utils/statusUtils';
import {
  statusLabel,
  statusOptions,
  subStatusForStatus,
  subStatusLabel,
  subStatusOptions,
} from '../../utils/webcheckinStatus';

// Edit Form Components - Defined outside main component to prevent focus loss

/**
 * Check-in Status Edit Form - Separate component to maintain focus
 * Defined OUTSIDE main component to prevent recreation on parent re-renders
 */
export const CheckInEditForm = ({ initialData, missingInfoTypeOptions, loading, onSave, onCancel }) => {
  // Use local state to prevent parent re-renders from causing focus loss
  const [formData, setFormData] = useState(initialData);
  const initialDataRef = useRef(initialData);
  
  // Sync from props on initial mount and when initialData changes
  useEffect(() => {
    const currentJson = JSON.stringify(initialDataRef.current);
    const newJson = JSON.stringify(initialData);
    if (currentJson !== newJson) {
      initialDataRef.current = initialData;
      setFormData(initialData);
    }
  }, [initialData]);
  
  const handleChange = useCallback((field, value) => {
    let newData = { ...formData, [field]: value };

    // A new status keeps the sub-status only if it still goes with it.
    if (field === 'status') {
      newData.subStatus = subStatusForStatus(value, formData.subStatus);
    }
    // Missing info only goes with the MISSING_INFO sub-status.
    if (newData.subStatus !== 'MISSING_INFO') {
      newData.missingInfo = [];
    }

    setFormData(newData);
  }, [formData]);
  
  const handleMissingInfoChange = useCallback((type) => {
    const current = formData.missingInfo || [];
    let newMissingInfo;
    if (current.includes(type)) {
      newMissingInfo = current.filter(t => t !== type);
    } else {
      newMissingInfo = [...current, type];
    }
    setFormData({ ...formData, missingInfo: newMissingInfo });
  }, [formData]);
  
  const handleSave = useCallback(() => {
    onSave(formData);
  }, [formData, onSave]);
  
  return (
    <div className="space-y-2">
      <CheckInStatusPresets
        disabled={loading}
        onSelect={(preset) => setFormData((current) => ({
          ...current,
          ...preset,
          missingInfo: [],
        }))}
      />
      <div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">Status</span>
        <select
          value={formData.status || ''}
          onChange={(e) => handleChange('status', e.target.value)}
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
          disabled={loading}
        >
          {!formData.status && <option value="">Select Status</option>}
          {statusOptions(formData.status).map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.invalid}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">Sub-Status</span>
        <select
          value={formData.subStatus || ''}
          onChange={(e) => handleChange('subStatus', e.target.value)}
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
          disabled={loading}
        >
          <option value="">None</option>
          {subStatusOptions(formData.status, formData.subStatus).map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.invalid}>{opt.label}</option>
          ))}
        </select>
      </div>
      {formData.subStatus === 'MISSING_INFO' && (
        <div>
          <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">Missing Info</span>
          <div className="space-y-1.5">
            {missingInfoTypeOptions.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={(formData.missingInfo || []).includes(opt.value)}
                  onChange={() => handleMissingInfoChange(opt.value)}
                  disabled={loading}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-500 focus:ring-blue-500/20"
                />
                <span className="text-gray-600">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">Message</span>
        <textarea
          value={formData.message || ''}
          onChange={(e) => handleChange('message', e.target.value)}
          className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white resize-none"
          rows={2}
          disabled={loading}
        />
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex-1 bg-blue-600 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-sm shadow-blue-500/25"
        >
          <Check className="w-3.5 h-3.5" />
          Save
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 bg-gray-100 text-gray-700 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <X className="w-3.5 h-3.5" />
          Cancel
        </button>
      </div>
    </div>
  );
};

/**
 * PNR Edit Form - Simple inline edit for PNR only
 * Defined OUTSIDE main component to prevent recreation on parent re-renders
 */
const PNREditForm = ({ initialValue, loading, onSave, onCancel }) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);
  
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  
  const handleSave = useCallback(() => {
    onSave(value);
  }, [value, onSave]);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 bg-white font-mono text-blue-600"
        disabled={loading}
        placeholder="Enter PNR"
      />
      <button
        onClick={handleSave}
        disabled={loading}
        className="p-1.5 rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 disabled:opacity-50"
        title="Save"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onCancel}
        disabled={loading}
        className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
        title="Cancel"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/**
 * Flight Details Tab Component for Business4Flyo
 * Enhanced with sidebar layout and premium visual design
 * @param {Object} props - Component props
 * @param {Array} props.flights - Array of flight objects
 * @param {Object} props.ticketData - Complete ticket data for sidebar
 * @param {number} props.ticketId - Ticket ID
 * @param {number} props.agentId - Agent ID
 * @param {Function} props.onUpdate - Callback to refresh data after update
 * @returns {JSX.Element} B4FFlightDetailsTab component
 */
const B4FFlightDetailsTab = ({ flights, ticketData, ticketId, agentId, onUpdate }) => {
  const [editingCheckIn, setEditingCheckIn] = useState(true); // Edit form shown by default
  const [editingTicket, setEditingTicket] = useState(false);
  const [editingTicketStatus, setEditingTicketStatus] = useState(false);
  const [checkInForm, setCheckInForm] = useState({});
  const [ticketForm, setTicketForm] = useState({});
  const [ticketStatusForm, setTicketStatusForm] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Expanded flights state - all collapsed by default
  const [expandedFlights, setExpandedFlights] = useState(new Set());
  
  // Initialize checkInForm with actual data when component mounts
  useEffect(() => {
    if (ticketData?.checkIn) {
      const { checkIn } = ticketData;
      const currentMissingInfo = checkIn.missingInfo || [];
      setCheckInForm({
        status: checkIn.status || '',
        subStatus: checkIn.subStatus || '',
        message: checkIn.message || '',
        optInBy: checkIn.optInBy || '',
        missingInfo: currentMissingInfo.map(item => item.type).filter(Boolean)
      });
    }
  }, [ticketData]);
  
  // Toggle flight expansion
  const toggleFlightExpand = useCallback((flightId) => {
    setExpandedFlights(prev => {
      const newSet = new Set(prev);
      if (newSet.has(flightId)) {
        newSet.delete(flightId);
      } else {
        newSet.add(flightId);
      }
      return newSet;
    });
  }, []);

  // Format duration from minutes to "Xh Ym"
  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could show toast here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Start editing check-in status
  const startEditingCheckIn = () => {
    if (!ticketData?.checkIn) return;
    const { checkIn } = ticketData;
    const currentMissingInfo = checkIn.missingInfo || [];
    setCheckInForm({
      status: checkIn.status || '',
      subStatus: checkIn.subStatus || '',
      message: checkIn.message || '',
      optInBy: checkIn.optInBy || '',
      missingInfo: currentMissingInfo.map(item => item.type).filter(Boolean)
    });
    setEditingCheckIn(true);
  };

  // Start editing PNR
  const startEditingTicket = () => {
    if (!ticketData) return;
    setTicketForm({
      pnr: ticketData.pnr || ''
    });
    setEditingTicket(true);
  };

  // Start editing ticket status
  const startEditingTicketStatus = () => {
    if (!ticketData) return;
    setTicketStatusForm((ticketData.ticketStatus || ticketData.bookingStatus || '').toLowerCase());
    setEditingTicketStatus(true);
  };

  // Cancel ticket status editing
  const cancelTicketStatusEditing = () => {
    setEditingTicketStatus(false);
    setTicketStatusForm('');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCheckIn(false);
    setEditingTicket(false);
    setEditingTicketStatus(false);
    setCheckInForm({});
    setTicketForm({});
    setTicketStatusForm('');
  };

  // Save check-in status with data parameter (for use by CheckInEditForm)
  const saveCheckInWithData = async (data) => {
    if (!agentId || !ticketId) {
      alert('Missing agentId or ticketId');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        agentId,
        ticketId: parseInt(ticketId),
        status: data.status,
        subStatus: data.subStatus,
        statusMessage: data.message,
        optInBy: data.optInBy,
        // Missing info only goes with the MISSING_INFO sub-status.
        missingInfo:
          data.subStatus === 'MISSING_INFO' ? (data.missingInfo || []).map(type => ({ type })) : [],
      };

      await updateBusiness4FlyoCheckIn(payload);
      setEditingCheckIn(false);
      setCheckInForm({});
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error updating check-in: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save PNR only
  const saveTicketWithData = async (pnrValue) => {
    if (!agentId || !ticketId) {
      alert('Missing agentId or ticketId');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        agentId,
        ticketId: parseInt(ticketId),
        pnr: pnrValue
      };

      if (ticketData.attachments) {
        payload.attachments = ticketData.attachments;
      }

      await updateBusiness4FlyoTicket(payload);
      setEditingTicket(false);
      setTicketForm({});
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error updating booking: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save ticket status only (changed fields)
  const saveTicketStatusWithData = async (statusValue) => {
    if (!agentId || !ticketId) {
      alert('Missing agentId or ticketId');
      return;
    }

    const currentStatus = (ticketData?.ticketStatus || ticketData?.bookingStatus || '').toLowerCase();
    if ((statusValue || '').toLowerCase() === currentStatus) {
      setEditingTicketStatus(false);
      setTicketStatusForm('');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        agentId,
        ticketId: parseInt(ticketId),
        ticketStatus: statusValue
      };

      await updateBusiness4FlyoTicket(payload);
      setEditingTicketStatus(false);
      setTicketStatusForm('');
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error updating ticket status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Missing Info type options
  const missingInfoTypeOptions = [
    { value: 'MISSING_PNR', label: 'Missing PNR' },
    { value: 'MISSING_PASSENGER', label: 'Missing Passenger' },
    { value: 'MISSING_PASSPORT', label: 'Missing Passport' },
  ];

  const ticketStatusOptions = [
    { value: 'confirmed', label: 'Confirmed', optionLabel: '🟢 Confirmed', badgeClass: 'bg-emerald-50 text-emerald-700 border border-emerald-100' },
    { value: 'pending', label: 'Pending', optionLabel: '🟡 Pending', badgeClass: 'bg-amber-50 text-amber-700 border border-amber-100' },
    { value: 'cancelled', label: 'Cancelled', optionLabel: '🔴 Cancelled', badgeClass: 'bg-red-50 text-red-700 border border-red-100' },
    { value: 'refunded', label: 'Refunded', optionLabel: '🔵 Refunded', badgeClass: 'bg-sky-50 text-sky-700 border border-sky-100' },
  ];


  // Static option arrays to pass to Sidebar
  const staticMissingInfoTypeOptions = missingInfoTypeOptions;
  const staticTicketStatusOptions = ticketStatusOptions;

  // Flight Card Component - Aesthetic & Futuristic Design
  const FlightCard = ({ flight, index, isExpanded, onToggleExpand }) => {
    const isOutbound = flight.flightType === 'outbound';
    const isReturn = flight.flightType === 'return';
    
    // Dynamic gradient based on flight type
    const getGradient = () => {
      if (isOutbound) return 'from-blue-500/10 via-blue-400/5 to-transparent';
      if (isReturn) return 'from-purple-500/10 via-purple-400/5 to-transparent';
      return 'from-gray-500/10 via-gray-400/5 to-transparent';
    };
    
    // Dynamic accent color
    const getAccentColor = () => {
      if (isOutbound) return 'blue';
      if (isReturn) return 'purple';
      return 'gray';
    };
    
    const accentColor = getAccentColor();
    
    return (
      <div className="group relative bg-white rounded-2xl border border-gray-100 shadow-lg shadow-gray-200/50 overflow-hidden hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
        {/* Subtle gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-br ${getGradient()} pointer-events-none`} />
        
        {/* Flight Header - Clickable to expand/collapse */}
        <div 
          className="relative px-5 py-4 cursor-pointer"
          onClick={onToggleExpand}
        >
          <div className="flex items-center justify-between">
            {/* Airline Info */}
            <div className="flex items-center gap-4">
              {/* Airline Logo/Icon with glow effect */}
              <div className={`relative w-12 h-12 rounded-xl bg-${accentColor}-50 flex items-center justify-center shadow-lg shadow-${accentColor}-500/20`}>
                {flight.airlineLogo ? (
                  <img 
                    src={flight.airlineLogo} 
                    alt={flight.airlinename || 'Airline'} 
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`hidden w-full h-full items-center justify-center text-${accentColor}-600 font-bold text-sm`}
                >
                  {flight.airlineIata || 'FL'}
                </div>
              </div>
              
              {/* Airline Name & Flight Number */}
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  {flight.airlinename || 'Unknown Airline'}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm font-medium text-gray-500">{flight.flightNumber || 'N/A'}</span>
                  <span className="w-1 h-1 rounded-full bg-gray-300" />
                  <span className={`text-xs font-semibold text-${accentColor}-600 bg-${accentColor}-50 px-2 py-0.5 rounded-full`}>
                    {isOutbound ? 'Outbound' : isReturn ? 'Return' : 'Flight'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Expand/Collapse Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              className={`p-2 rounded-xl bg-${accentColor}-50 text-${accentColor}-600 hover:bg-${accentColor}-100 hover:scale-105 transition-all duration-200`}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Flight Route Details - Only show when expanded */}
        {isExpanded && (
          <div className="relative px-5 pb-5">
            {/* Route Container */}
            <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 border border-gray-100">
              <div className="flex items-center gap-4">
                {/* Departure */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">From</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900 tracking-tight">
                      {flight.departure?.iata || 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 font-medium truncate">
                    {flight.departure?.city || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {flight.departure?.country || ''}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="bg-white rounded-lg px-2.5 py-1 shadow-sm border border-gray-100">
                      <span className="text-sm font-semibold text-gray-800">{flight.departure?.time || '--:--'}</span>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(flight.departure?.date)}</span>
                  </div>
                  {flight.departure?.terminal && (
                    <div className="mt-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium text-${accentColor}-600 bg-${accentColor}-50 px-2 py-1 rounded-lg`}>
                        <span>Terminal</span>
                        <span className="font-bold">{flight.departure.terminal}</span>
                      </span>
                    </div>
                  )}
                </div>

                {/* Flight Path - Animated */}
                <div className="flex flex-col items-center px-2">
                  {/* Duration Badge */}
                  <div className={`mb-3 px-3 py-1 rounded-full bg-${accentColor}-100 text-${accentColor}-700`}>
                    <span className="text-xs font-semibold">{formatDuration(flight.duration)}</span>
                  </div>
                  
                  {/* Flight Line */}
                  <div className="relative w-24 h-12 flex items-center">
                    {/* Dotted line */}
                    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-0.5 border-t-2 border-dashed border-gray-300" />
                    
                    {/* Plane icon with rotation */}
                    <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-${accentColor}-500 shadow-lg shadow-${accentColor}-500/30 flex items-center justify-center`}>
                      <Plane className="w-5 h-5 text-white" style={{ transform: 'rotate(90deg)' }} />
                    </div>
                    
                    {/* Start point */}
                    <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-${accentColor}-500 border-2 border-white shadow-md`} />
                    
                    {/* End point */}
                    <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-${accentColor}-500 border-2 border-white shadow-md`} />
                  </div>
                  
                  {/* Clock icon */}
                  <div className="mt-2 flex items-center gap-1 text-gray-400">
                    <Clock className="w-3 h-3" />
                  </div>
                </div>

                {/* Arrival */}
                <div className="flex-1 min-w-0 text-right">
                  <div className="flex items-center justify-end gap-1.5 text-gray-400 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider">To</span>
                    <MapPin className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex items-baseline justify-end gap-2">
                    <span className="text-3xl font-bold text-gray-900 tracking-tight">
                      {flight.arrival?.iata || 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 font-medium truncate">
                    {flight.arrival?.city || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {flight.arrival?.country || ''}
                  </p>
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <span className="text-xs text-gray-400">{formatDate(flight.arrival?.date)}</span>
                    <div className="bg-white rounded-lg px-2.5 py-1 shadow-sm border border-gray-100">
                      <span className="text-sm font-semibold text-gray-800">{flight.arrival?.time || '--:--'}</span>
                    </div>
                  </div>
                  {flight.arrival?.terminal && (
                    <div className="mt-2 flex justify-end">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium text-${accentColor}-600 bg-${accentColor}-50 px-2 py-1 rounded-lg`}>
                        <span>Terminal</span>
                        <span className="font-bold">{flight.arrival.terminal}</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Info - Modern Pills */}
              {(flight.aircraft || flight.bookingClass) && (
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-3">
                  {flight.aircraft && (
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-100">
                      <Info className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">Aircraft</span>
                      <span className="text-xs font-bold text-gray-800">{flight.aircraft}</span>
                    </div>
                  )}
                  {flight.bookingClass && (
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-100">
                      <span className="text-xs font-medium text-gray-600">Class</span>
                      <span className="text-xs font-bold text-gray-800 capitalize">{flight.bookingClass}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Agent Banner Component
  const AgentBanner = () => {
    if (!ticketData?.agent) return null;
    const { agent } = ticketData;
    
    return (
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-xl p-3 mb-2 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <User className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Agent Information</h3>
              <p className="text-xs text-gray-600">{agent.name || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {agent.email && (
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <Mail className="w-3.5 h-3.5 text-purple-500" />
                <span>{agent.email}</span>
              </div>
            )}
            {agent.mobile && (
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <Phone className="w-3.5 h-3.5 text-purple-500" />
                <span>{agent.mobile}</span>
              </div>
            )}
            {agent.agentId && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">ID:</span>
                <span className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                  {agent.agentId}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!flights || flights.length === 0) {
    return (
      <div className="flex gap-2">
        <div className="flex-1">
          <AgentBanner />
          <div className="bg-white rounded border border-gray-200 shadow-sm p-6 text-center">
            <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-2">
              <Plane className="w-5 h-5 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-0.5">No Flight Information</h3>
            <p className="text-xs text-gray-500">No flight details are available for this ticket.</p>
          </div>
        </div>
        <Sidebar 
          ticketData={ticketData}
          editingCheckIn={editingCheckIn}
          editingTicket={editingTicket}
          editingTicketStatus={editingTicketStatus}
          checkInForm={checkInForm}
          ticketForm={ticketForm}
          ticketStatusForm={ticketStatusForm}
          loading={loading}
          missingInfoTypeOptions={staticMissingInfoTypeOptions}
          ticketStatusOptions={staticTicketStatusOptions}
          setCheckInForm={setCheckInForm}
          setTicketStatusForm={setTicketStatusForm}
          saveCheckInWithData={saveCheckInWithData}
          saveTicketWithData={saveTicketWithData}
          saveTicketStatusWithData={saveTicketStatusWithData}
          startEditingCheckIn={startEditingCheckIn}
          startEditingTicket={startEditingTicket}
          startEditingTicketStatus={startEditingTicketStatus}
          cancelEditing={cancelEditing}
          cancelTicketStatusEditing={cancelTicketStatusEditing}
          copyToClipboard={copyToClipboard}
        />
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      {/* Main Content - Flight Cards */}
      <div className="flex-1 min-w-0">
        <AgentBanner />
        <div className="space-y-2">
          {flights.map((flight, index) => (
            <FlightCard 
              key={flight.flightId || index} 
              flight={flight} 
              index={index}
              isExpanded={expandedFlights.has(flight.flightId)}
              onToggleExpand={() => toggleFlightExpand(flight.flightId)}
            />
          ))}
        </div>
      </div>

      {/* Right Sidebar */}
      <Sidebar 
        ticketData={ticketData}
        editingCheckIn={editingCheckIn}
        editingTicket={editingTicket}
        editingTicketStatus={editingTicketStatus}
        checkInForm={checkInForm}
        ticketForm={ticketForm}
        ticketStatusForm={ticketStatusForm}
        loading={loading}
        missingInfoTypeOptions={staticMissingInfoTypeOptions}
        ticketStatusOptions={staticTicketStatusOptions}
        setCheckInForm={setCheckInForm}
        setTicketStatusForm={setTicketStatusForm}
        saveCheckInWithData={saveCheckInWithData}
        saveTicketWithData={saveTicketWithData}
        saveTicketStatusWithData={saveTicketStatusWithData}
        startEditingCheckIn={startEditingCheckIn}
        startEditingTicket={startEditingTicket}
        startEditingTicketStatus={startEditingTicketStatus}
        cancelEditing={cancelEditing}
        cancelTicketStatusEditing={cancelTicketStatusEditing}
        copyToClipboard={copyToClipboard}
      />
    </div>
  );
};

// Section Card Component - defined outside main component
const SectionCard = ({ title, icon: Icon, bgColor = 'bg-blue-50', children, action }) => (
  <div className="bg-white/70 backdrop-blur-md rounded-xl border border-gray-100 shadow-lg shadow-gray-200/50 overflow-hidden hover:shadow-xl hover:shadow-gray-200/60 transition-all duration-300">
    <div className={`px-3 py-2.5 ${bgColor} border-b border-gray-100 flex items-center justify-between`}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-white/80 flex items-center justify-center shadow-sm">
          <Icon className="w-3.5 h-3.5 text-gray-700" />
        </div>
        <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{title}</h3>
      </div>
      {action && (
        <div className="flex items-center gap-0.5">
          {action}
        </div>
      )}
    </div>
    <div className="p-3">
      {children}
    </div>
  </div>
);

// Info Row Component - defined outside main component
const InfoRow = ({ label, value, badge = null, copyValue = null, onCopy, alwaysShowCopy = false }) => (
  <div className="flex items-center justify-between py-1.5 group">
    <span className="text-xs text-gray-500 font-medium">{label}</span>
    <div className="flex items-center gap-1.5">
      {badge ? (
        badge
      ) : (
        <span className="text-xs font-semibold text-gray-800">{value || 'N/A'}</span>
      )}
      {copyValue && onCopy && (
        <button 
          onClick={() => onCopy(copyValue, label)}
          className={`p-1 rounded-md bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-all ${alwaysShowCopy ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
          title={`Copy ${label}`}
        >
          <Copy className="w-3 h-3" />
        </button>
      )}
    </div>
  </div>
);

// Status badge helper
const getStatusBadge = (status) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusTailwindClasses(status)}`}>
    {statusLabel(status)}
  </span>
);

const getTicketStatusBadgeClasses = (status, ticketStatusOptions) => {
  const normalizedStatus = (status || '').toLowerCase();
  const option = ticketStatusOptions.find((item) => item.value === normalizedStatus);
  return option?.badgeClass || 'bg-gray-50 text-gray-700 border border-gray-100';
};

// Sidebar component - defined outside main component to prevent re-renders
const Sidebar = ({
  ticketData,
  editingCheckIn,
  editingTicket,
  editingTicketStatus,
  checkInForm,
  ticketForm,
  ticketStatusForm,
  loading,
  missingInfoTypeOptions,
  ticketStatusOptions,
  setCheckInForm,
  setTicketStatusForm,
  saveCheckInWithData,
  saveTicketWithData,
  saveTicketStatusWithData,
  startEditingCheckIn,
  startEditingTicket,
  startEditingTicketStatus,
  cancelEditing,
  cancelTicketStatusEditing,
  copyToClipboard
}) => {
  if (!ticketData) return null;

  const { checkIn, pnr } = ticketData;
  const ticketStatus = ticketData.ticketStatus || ticketData.bookingStatus || '';
  
  // Extract client info from ticketData
  const clientPhone = ticketData.clientPhone || (ticketData.clients && ticketData.clients[0]?.phone) || '';
  const clientPhoneCountryCode = ticketData.clientPhoneCountryCode || (ticketData.clients && ticketData.clients[0]?.phoneCountryCode) || '';
  const clientEmail = ticketData.clientEmail || (ticketData.clients && ticketData.clients[0]?.email) || '';
  
  // Extract agent info from ticketData
  const agentPhone = ticketData.agentPhone || ticketData.agent?.phone || ticketData.agent?.mobile || '';
  const agentPhoneCountryCode = ticketData.agentPhoneCountryCode || ticketData.agent?.countryCode || ticketData.agent?.phoneCountryCode || '';
  const agentEmail = ticketData.agentEmail || ticketData.agent?.email || '';
  
  // Extract all travellers from first flight
  const firstFlightTravellers = (ticketData.flights && ticketData.flights[0]?.travellers) || [];
  
  return (
    <div className="w-96 flex-shrink-0 space-y-2">
      {/* Check-in Status Card */}
      {checkIn && (
        <SectionCard 
          title="Check-in Status" 
          icon={Activity}
          bgColor="bg-blue-50"
          action={!editingCheckIn && (
            <button 
              onClick={startEditingCheckIn}
              className="p-1.5 rounded-lg bg-white text-gray-600 hover:text-blue-600 transition-all shadow-sm border border-gray-100"
              title="Edit Check-in Status"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          )}
        >
          {editingCheckIn ? (
            <CheckInEditForm
              initialData={checkInForm}
              missingInfoTypeOptions={missingInfoTypeOptions}
              loading={loading}
              onSave={(data) => {
                setCheckInForm(data);
                saveCheckInWithData(data);
              }}
              onCancel={cancelEditing}
            />
          ) : (
            <div className="space-y-1">
              <InfoRow 
                label="Status" 
                badge={getStatusBadge(checkIn.status)}
              />
              {checkIn.subStatus && (
                <InfoRow label="Sub-Status" value={subStatusLabel(checkIn.subStatus)} />
              )}
              {checkIn.optInBy && (
                <InfoRow 
                  label="Opt-in By" 
                  badge={
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      checkIn.optInBy === 'client' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                      checkIn.optInBy === 'agent' ? 'bg-green-50 text-green-600 border border-green-100' :
                      'bg-amber-50 text-amber-600 border border-amber-100'
                    }`}>
                      {checkIn.optInBy}
                    </span>
                  }
                />
              )}
              {checkIn.missingInfo && checkIn.missingInfo.length > 0 && (
                <div className="pt-1">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1.5">Missing Info</span>
                  <div className="flex flex-wrap gap-1">
                    {checkIn.missingInfo.map((info, idx) => (
                      <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100">
                        {info.type}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {checkIn.message && (
                <div className="pt-1">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">Message</span>
                  <p className="text-xs text-gray-600 leading-relaxed bg-gray-50/50 rounded-lg p-2">{checkIn.message}</p>
                </div>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* Ticket Summary Card */}
      <SectionCard 
        title="Ticket Info" 
        icon={FileText}
        bgColor="bg-emerald-50"
      >
        <div className="space-y-1">
          {/* PNR Row with Edit */}
          {editingTicket ? (
            <div className="py-1">
              <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">PNR</span>
              <PNREditForm
                initialValue={ticketForm.pnr || ''}
                loading={loading}
                onSave={(value) => saveTicketWithData(value)}
                onCancel={cancelEditing}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between py-1.5">
              <span className="text-xs text-gray-500 font-medium">PNR</span>
              <div className="flex items-center gap-1.5">
                {pnr ? (
                  <span className="text-xs font-mono font-semibold text-blue-600">{pnr}</span>
                ) : (
                  <span className="text-xs font-semibold text-gray-400">N/A</span>
                )}
                {pnr && (
                  <button 
                    onClick={() => copyToClipboard(pnr, 'PNR')}
                    className="p-1 rounded-md bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-all"
                    title="Copy PNR"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
                <button 
                  onClick={startEditingTicket}
                  className="p-1 rounded-md bg-gray-50 text-gray-400 hover:bg-emerald-50 hover:text-emerald-500 transition-all"
                  title="Edit PNR"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Ticket Status Row with Edit */}
          {editingTicketStatus ? (
            <div className="py-1.5">
              <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">Ticket Status</span>
              <select
                value={ticketStatusForm}
                onChange={(event) => setTicketStatusForm(event.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 bg-white"
                disabled={loading}
              >
                <option value="">Select Ticket Status</option>
                {ticketStatusOptions.map((statusOption) => (
                  <option key={statusOption.value} value={statusOption.value}>
                    {statusOption.optionLabel || statusOption.label}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex items-center gap-2">
                {(ticketStatusForm || '').toLowerCase() !== (ticketStatus || '').toLowerCase() && (
                  <button
                    type="button"
                    onClick={() => saveTicketStatusWithData(ticketStatusForm)}
                    disabled={loading}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Save Changes
                  </button>
                )}
                <button
                  type="button"
                  onClick={cancelTicketStatusEditing}
                  disabled={loading}
                  className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between py-1.5">
              <span className="text-xs text-gray-500 font-medium">Ticket Status</span>
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${getTicketStatusBadgeClasses(ticketStatus, ticketStatusOptions)}`}
                >
                  {(ticketStatus || 'N/A').toUpperCase()}
                </span>
                <button
                  onClick={startEditingTicketStatus}
                  className="p-1 rounded-md bg-gray-50 text-gray-400 hover:bg-emerald-50 hover:text-emerald-500 transition-all"
                  title="Edit Ticket Status"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
          
          {/* Client Phone - Copy only */}
          {(clientPhoneCountryCode || clientPhone) && (
            <InfoRow 
              label="Client Phone" 
              value={`${clientPhoneCountryCode || ''} ${clientPhone || ''}`}
              copyValue={`${clientPhoneCountryCode || ''}${clientPhone || ''}`}
              onCopy={copyToClipboard}
              alwaysShowCopy={true}
            />
          )}
          
          {/* Client Email - Copy only */}
          {clientEmail && (
            <InfoRow 
              label="Client Email" 
              value={clientEmail}
              copyValue={clientEmail}
              onCopy={copyToClipboard}
              alwaysShowCopy={true}
            />
          )}
          
          {/* Agent Phone - Copy only */}
          {(agentPhoneCountryCode || agentPhone) && (
            <InfoRow 
              label="Agent Phone" 
              value={`${agentPhoneCountryCode || ''} ${agentPhone || ''}`}
              copyValue={`${agentPhoneCountryCode || ''}${agentPhone || ''}`}
              onCopy={copyToClipboard}
              alwaysShowCopy={true}
            />
          )}
          
          {/* Agent Email - Copy only */}
          {agentEmail && (
            <InfoRow 
              label="Agent Email" 
              value={agentEmail}
              copyValue={agentEmail}
              onCopy={copyToClipboard}
              alwaysShowCopy={true}
            />
          )}
          
          {/* Travellers from first flight - Copy only */}
          {firstFlightTravellers.length > 0 && (
            <div className="py-1.5">
              <div className="flex items-start justify-between">
                <span className="text-xs text-gray-500 font-medium pt-0.5">Travellers</span>
                <div className="flex flex-col items-end gap-1.5">
                  {firstFlightTravellers.map((traveller, index) => {
                    return (
                      traveller.lastname && (
                        <div key={traveller.travellerId || index} className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-gray-800">{traveller.lastname}</span>
                          <button 
                            onClick={() => copyToClipboard(traveller.lastname, 'Traveller Last Name')}
                            className="p-1 rounded-md bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-all"
                            title={`Copy ${traveller.lastname}`}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      )
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </SectionCard>


    </div>
  );
};

export default B4FFlightDetailsTab;
